import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Check, Wind, Droplet, Leaf, Home, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';

const MaisonBiosourceePage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const advantages = [
    {
      icon: Wind,
      title: 'Air sain et purifié',
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800',
      items: [
        'VMC intelligente : Une ventilation maîtrisée pour un air pur et renouvelé en continu',
        'Matériaux perspirants : Une régulation naturelle de l’humidité pour un confort optimal',
        'Isolants sans COV (composés organiques volatiles) : Des matériaux sains, exempts de composés organiques volatils'
      ]
    },
    {
      icon: Leaf,
      title: 'Des matériaux naturels et locaux',
      image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800',
      items: [
        'Briques en terre cuite : Isolation thermique et acoustique performante, fabriquées en Provence-Alpes-Côte d’Azur',
        'Hourdis bois : Bois labellisé issu du Massif Central, avec des performances optimales',
        'Tuiles en terre cuite et façade à enduit minéral : Des matières premières naturelles et recyclables, durables dans le temps'
      ]
    },
    {
      icon: Droplet,
      title: 'Gestion durable de l’eau',
      image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
      items: [
        'Récupérateur d’eaux pluviales : Économie et optimisation sur votre utilisation de l’eau',
        'Récupérateur de chaleur: Réutilisation de l’énergie pour chauffer l’eau de la douche. Votre 4ème douche devient gratuite !',
        'Adoucisseur d’eau : Protection de vos équipements et bien-être pour votre peau'
      ]
    },
    {
      icon: Home,
      title: 'Confort thermique et économies d’énergie',
      image: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800',
      items: [
        'Isolation en laine biosourcée (coton, lin, chanvre) : Régulation naturelle de l’humidité',
        'Isolation des combles en fibres de cellulose : Confort en été comme en hiver',
        'Poêle à granulés : Une solution de chauffage performante, esthétique et économique',
        'Box domotique connectée : Une gestion intuitive pour optimiser la température et donc réduire vos dépenses énergétiques',
        'Panneaux photovoltaïques : Consommez votre propre énergie verte et revendez le surplus pour un avenir plus durable'
      ]
    }
  ];

  const gifts = [
    {
      title: 'Briques en terre cuite',
      description: 'un matériau naturel, durable et performant pour une maison saine'
    },
    {
      title: 'Isolation des murs périphériques',
      description: 'une barrière thermique et acoustique incontournable pour limiter les pertes d’énergie'
    },
    {
      title: 'Isolation des cloisons',
      description: 'pour un confort thermo-acoustique qui améliore votre quotidien'
    },
    {
      title: 'Isolation des combles',
      description: 'indispensable pour optimiser l’efficacité énergétique de votre maison'
    }
  ];

  const faqs = [
    {
      question: 'Qu’est-ce qu’une maison biosourcée ?',
      answer: 'C’est une maison construite avec des matériaux naturels, renouvelables et à faible impact écologique, pour un habitat sain et durable.'
    },
    {
      question: 'Quels sont les avantages des matériaux biosourcés ?',
      answer: 'Ils améliorent en effet l’isolation thermique et acoustique, assurent un air intérieur sain et réduisent l’empreinte carbone de la construction.'
    },
    {
      question: 'Les maisons biosourcées sont-elles adaptées à toutes les régions ?',
      answer: 'Oui ! Grâce à des matériaux performants et adaptés, elles répondent donc aux exigences climatiques de chaque région.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div
        className="relative h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1600)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-white">
            <div className="flex items-center text-sm mb-4">
              <Link to="/" className="opacity-90 hover:opacity-100">Accueil</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span>Maison biosourcée</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold">LA MAISON BIOSOURCEE : SANTÉ, ÉCOLOGIE ET CONFORT</h1>
          </div>
        </div>
      </div>

      {/* 40 ans - 4 Cadeaux Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Pour nos 40 ans, 4 cadeaux pour votre future maison biosourcée</h2>
            <p className="text-xl text-gray-600 mb-8">
              <strong>40 ans d’expertise, une maison biosourcée et 4 cadeaux exclusifs pour vous !</strong>
            </p>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-8">
              Pour célébrer <strong>nos 40 ans</strong>, MRTP SARL lance sa <strong>maison biosourcée</strong> et vous offre <strong>4 cadeaux</strong> essentiels pour votre projet de construction neuve. Ces matériaux et équipements ont été soigneusement sélectionnés car ils jouent un rôle clé dans la qualité et la durabilité de votre maison, et ne peuvent être ajoutés ou modifiés par la suite :
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {gifts.map((gift, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#c4d600] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <Check className="w-5 h-5 text-gray-800" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">{gift.title}</h3>
                    <p className="text-sm text-gray-600">{gift.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Introduction Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">UN HABITAT PENSE POUR AUJOURD’HUI ET DEMAIN</h2>
          <p className="text-lg text-gray-600 mb-4">
            Chez <strong>MRTP SARL</strong>, nous comprenons que la construction d’une maison ne concerne pas seulement les matériaux, mais aussi l’impact sur <strong>votre santé</strong>, sur <strong>l’environnement</strong> et sur <strong>vos factures d’énergie</strong>. C’est pourquoi nous vous proposons <strong>la maison biosourcée</strong> : une maison écologique, conçue pour offrir un habitat <strong>durable</strong>, <strong>sain</strong>, et <strong>économe en énergie</strong>.
          </p>
          <p className="text-lg text-gray-600 mb-8">
            Notre démarche est simple : allier tradition et innovation en intégrant des <strong>matériaux biosourcés</strong>, issus de la nature, recyclés et ayant une faible <strong>empreinte</strong> <strong>carbone</strong>. Avec la maison biosourcée, vous réalisez donc un choix réfléchi, à la fois pour votre <strong>santé</strong>, pour la <strong>planète</strong> et pour votre <strong>portefeuille</strong>.
          </p>
          
          <div className="bg-[#c4d600] rounded-lg p-8 text-center">
            <p className="text-xl font-bold text-gray-800 mb-4">Opter pour une maison biosourcée, c’est alors, choisir un équilibre parfait entre :</p>
            <div className="space-y-2 text-gray-800">
              <p className="text-lg">✅ Un environnement sain pour votre famille</p>
              <p className="text-lg">✅ Des économies d’énergie grâce à une isolation optimisée</p>
              <p className="text-lg">✅ Une démarche responsable pour la planète</p>
            </div>
          </div>
        </div>
      </div>

      {/* Advantages Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c4d600] font-semibold mb-2">LES AVANTAGES D’UNE MAISON BIOSOURCEE</p>
            <h2 className="text-4xl font-bold text-gray-800">UN HABITAT RESPECTUEUX DE L’ENVIRONNEMENT ET DE VOTRE BIEN-ETRE</h2>
          </div>

          <div className="space-y-16">
            {advantages.map((advantage, index) => {
              const Icon = advantage.icon;
              return (
                <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-[#c4d600] rounded-full flex items-center justify-center mr-4">
                        <Icon className="w-8 h-8 text-gray-800" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800">{advantage.title}</h3>
                    </div>
                    <div className="space-y-4">
                      {advantage.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-start">
                          <Check className="w-5 h-5 text-[#c4d600] mr-3 mt-1 flex-shrink-0" />
                          <p className="text-gray-600">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={`relative h-80 rounded-lg overflow-hidden shadow-xl ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <img
                      src={advantage.image}
                      alt={advantage.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Engagement Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-8">UN ENGAGEMENT POUR VOTRE SANTE ET LA PLANETE</h2>
          <p className="text-lg text-gray-600 mb-6">Nos maisons biosourcées sont conçues pour :</p>
          <div className="space-y-3 text-left max-w-2xl mx-auto">
            <div className="flex items-start">
              <Check className="w-6 h-6 text-[#c4d600] mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">Améliorer la qualité de l’air intérieur</span>
            </div>
            <div className="flex items-start">
              <Check className="w-6 h-6 text-[#c4d600] mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">Minimiser les substances nocives</span>
            </div>
            <div className="flex items-start">
              <Check className="w-6 h-6 text-[#c4d600] mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-gray-700">Réduire vos émissions de CO2 grâce à des matériaux responsables</span>
            </div>
          </div>
          <p className="text-lg text-gray-600 mt-8">
            Chaque élément contribue à un habitat durable et sain, pensé pour le bien-être de votre famille.
          </p>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="py-16 bg-[#c4d600]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">POURQUOI CHOISIR UNE MAISON BIOSOURCEE AVEC LA MAISON DES COMPAGNONS ?</h2>
          <p className="text-lg text-gray-800 mb-8">
            En faisant le choix d’une maison biosourcée, vous faites alors un pas décisif vers un habitat plus sain, plus économique et plus respectueux de l’environnement. Chez MRTP SARL, nous vous offrons en effet l’opportunité d’acquérir une maison biosourcée et durable adaptée aux enjeux environnementaux actuels.
          </p>
          
          <div className="bg-white rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Avec nous, faites le choix d’une maison qui :</h3>
            <div className="space-y-3 text-left max-w-2xl mx-auto">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-[#c4d600] rounded-full flex items-center justify-center mr-3">
                  <Home className="w-5 h-5 text-gray-800" />
                </div>
                <span className="text-gray-700 font-semibold">Prend soin de votre santé</span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-[#c4d600] rounded-full flex items-center justify-center mr-3">
                  <Leaf className="w-5 h-5 text-gray-800" />
                </div>
                <span className="text-gray-700 font-semibold">Préserve la planète</span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-[#c4d600] rounded-full flex items-center justify-center mr-3">
                  <Check className="w-5 h-5 text-gray-800" />
                </div>
                <span className="text-gray-700 font-semibold">Offre confort et économies chaque jour de l’année</span>
              </div>
            </div>
          </div>
          
          <Button asChild className="bg-gray-800 hover:bg-gray-900 text-white font-semibold text-lg px-8 py-6">
            <Link to="/contact">Contactez-nous pour votre projet</Link>
          </Button>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c4d600] font-semibold mb-2">FAQ</p>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">LES QUESTIONS FREQUENTES</h2>
            <p className="text-lg text-gray-600">
              Vous avez des questions sur ce type de construction respectueuse de l’environnement ?
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-100 transition-colors rounded-lg"
                >
                  <h3 className="font-semibold text-gray-800 pr-4">{faq.question}</h3>
                  {openFaq === index ? (
                    <Minus className="w-5 h-5 text-[#c4d600] flex-shrink-0" />
                  ) : (
                    <Plus className="w-5 h-5 text-[#c4d600] flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaisonBiosourceePage;